import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Image as ImageIcon, ListChecks, FileText, RefreshCw, Trash2, Upload, Loader2, ArrowLeft, Copy,
  ShieldCheck, LayoutDashboard, Search, Archive, ArchiveRestore, Phone, Mail, ArrowUpDown, Inbox,
  Link2, ExternalLink, Globe, Menu, X,
} from "lucide-react";
import {
  getToken, logout, listLeads, getLeadStats, updateLead, archiveLead, deleteLead,
  getAllContent, setContent, uploadMedia, listMedia, deleteMedia,
  me, changePassword, mfaSetup, mfaEnable, mfaDisable, mfaCancel,
} from "../../lib/adminApi";
import { BrandIcon, PUBLIC_PLATFORMS } from "../../components/SocialLinks";

// ==================== CRM pipeline (canonical stages) ====================
const PIPELINE = [
  { id: "new", label: "New", cls: "bg-[#99582A] text-white" },
  { id: "contacted", label: "Contacted", cls: "bg-[#2563EB] text-white" },
  { id: "estimate_scheduled", label: "Estimate Scheduled", cls: "bg-[#7C3AED] text-white" },
  { id: "estimate_sent", label: "Estimate Sent", cls: "bg-[#0891B2] text-white" },
  { id: "follow_up", label: "Follow-Up", cls: "bg-[#D97706] text-white" },
  { id: "won", label: "Won", cls: "bg-[#15803D] text-white" },
  { id: "lost", label: "Lost", cls: "bg-[#6B7280] text-white" },
];
const LEGACY_STATUS = { estimate_completed: "estimate_sent", proposal_sent: "estimate_sent" };
const normStatus = (s) => LEGACY_STATUS[(s || "new").toLowerCase()] || (s || "new").toLowerCase();
const statusMeta = (s) =>
  PIPELINE.find((p) => p.id === normStatus(s)) ||
  { id: normStatus(s), label: normStatus(s).replace(/_/g, " "), cls: "bg-[#F1EBE3] text-[#4A4A4A]" };

function StatusBadge({ status, testid }) {
  const m = statusMeta(status);
  return (
    <span data-testid={testid} className={`inline-block px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${m.cls}`}>
      {m.label}
    </span>
  );
}

const SOURCE_LABELS = {
  ai_chat: "AI Chat",
  contact_form: "Contact Form",
  estimate_form: "Estimate Form",
  financing_form: "Financing",
  financing_interest: "Financing",
};
const sourceLabel = (s) => SOURCE_LABELS[s] || (s ? String(s).replace(/_/g, " ") : "Website");

function SourceBadge({ source, testid }) {
  const isAi = source === "ai_chat";
  return (
    <span
      data-testid={testid}
      className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide border ${isAi ? "border-[#99582A] text-[#99582A]" : "border-[#D8D2C8] text-[#777]"}`}
    >
      {sourceLabel(source)}
    </span>
  );
}

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "leads", label: "Leads", icon: ListChecks },
  { id: "quicklinks", label: "Quick Links", icon: Link2 },
  { id: "hero", label: "Homepage Hero", icon: ImageIcon },
  { id: "aerial", label: "Aerial Showcase", icon: ImageIcon },
  { id: "timbertech", label: "TimberTech Page", icon: FileText },
  { id: "content", label: "Content", icon: FileText },
  { id: "ai", label: "AI Knowledge", icon: FileText },
  { id: "media", label: "Media Library", icon: ImageIcon },
  { id: "security", label: "Security", icon: ShieldCheck },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [navOpen, setNavOpen] = useState(false);
  if (!getToken()) return <Navigate to="/admin/login" replace />;

  const activeTab = TABS.find((t) => t.id === tab) || TABS[0];
  const go = (id) => { setTab(id); setNavOpen(false); };

  return (
    <div className="min-h-screen bg-[#FBF8F3] text-[#111]">
      <header className="bg-white border-b border-[#E7E2D9] px-4 sm:px-6 lg:px-10 py-3 lg:py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button
            data-testid="admin-nav-toggle"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 -ml-1 border border-[#E7E2D9] rounded-md active:bg-[#F1EBE3]"
          >
            <Menu size={20} />
          </button>
          <img src="/logo.png" alt="Zarco's" className="h-9 lg:h-10 w-auto" />
          <span className="font-display text-base lg:text-lg truncate">
            <span className="hidden lg:inline">Admin</span>
            <span className="lg:hidden flex items-center gap-1.5 text-[#99582A]"><activeTab.icon size={15} /> {activeTab.label}</span>
          </span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/" className="text-sm text-[#4A4A4A] hover:text-[#99582A] flex items-center gap-1">
            <ArrowLeft size={14} /> <span className="hidden sm:inline">View site</span>
          </Link>
          <button
            data-testid="admin-logout"
            onClick={() => { logout(); window.location.href = "/admin/login"; }}
            className="sm:ml-4 border border-[#E7E2D9] hover:border-[#99582A] px-3 py-2 text-sm flex items-center gap-2"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      {/* Mobile slide-in drawer nav */}
      {navOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="admin-mobile-drawer" onClick={() => setNavOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <nav className="absolute left-0 top-0 h-full w-[80%] max-w-xs bg-white shadow-xl p-3 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-2 py-2 mb-2 border-b border-[#E7E2D9]">
              <span className="font-display text-lg">Menu</span>
              <button data-testid="admin-drawer-close" onClick={() => setNavOpen(false)} aria-label="Close menu" className="w-9 h-9 inline-flex items-center justify-center"><X size={20} /></button>
            </div>
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-drawer-tab-${t.id}`}
                  onClick={() => go(t.id)}
                  className={`w-full text-left px-4 py-3.5 text-base font-medium flex items-center gap-3 rounded-md ${active ? "bg-[#111] text-white" : "hover:bg-[#FBF8F3]"}`}
                >
                  <Icon size={18} /> {t.label}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6 p-4 sm:p-6 lg:p-10 overflow-x-hidden">
        <aside className="hidden lg:block lg:col-span-3 min-w-0">
          <nav className="bg-white border border-[#E7E2D9] p-2 flex flex-col gap-1 sticky top-24">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`text-left px-4 py-3 text-sm font-medium flex items-center gap-3 whitespace-nowrap ${
                    active ? "bg-[#111] text-white" : "hover:bg-[#FBF8F3]"
                  }`}
                >
                  <Icon size={16} /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="lg:col-span-9 min-w-0 space-y-8">
          {tab === "overview" && <OverviewPanel onOpenLeads={() => setTab("leads")} />}
          {tab === "leads" && <LeadsPanel />}
          {tab === "quicklinks" && <QuickLinksPanel onEdit={() => setTab("content")} />}
          {tab === "hero" && <HeroPanel />}
          {tab === "aerial" && <AerialPanel />}
          {tab === "timbertech" && <TimberTechPanel />}
          {tab === "content" && <ContentPanel />}
          {tab === "ai" && <AIKnowledgePanel />}
          {tab === "media" && <MediaPanel />}
          {tab === "security" && <SecurityPanel />}
        </main>
      </div>
    </div>
  );
}

// ==================== OVERVIEW ====================
function StatCard({ label, value, accent, testid, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left bg-white border border-[#E7E2D9] p-5 hover:border-[#99582A] transition-colors"
    >
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#666]">{label}</p>
      <p className={`font-display text-4xl mt-2 tracking-tight ${accent || "text-[#111]"}`}>{value}</p>
    </button>
  );
}

function OverviewPanel({ onOpenLeads }) {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, leads] = await Promise.all([getLeadStats(), listLeads(false)]);
      setStats(s);
      setRecent(leads);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading || !stats) {
    return <div className="py-16 text-center text-[#666]" data-testid="admin-overview-panel"><Loader2 className="animate-spin inline mr-2" size={16}/> Loading…</div>;
  }

  const bs = stats.by_status || {};
  const newCount = bs.new || 0;
  const newLeads = recent.filter((l) => normStatus(l.status) === "new");

  return (
    <section data-testid="admin-overview-panel" className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Zarco's Decks · Owner CRM</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight mt-1">Overview</h2>
          <p className="text-sm text-[#666] mt-1">{stats.total_active} active lead{stats.total_active === 1 ? "" : "s"} · {stats.last_7_days} new this week · {stats.last_30_days} in last 30 days</p>
        </div>
        <button onClick={load} className="border border-[#E7E2D9] px-3 py-2 text-sm flex items-center gap-2 hover:border-[#99582A]">
          <RefreshCw size={14} /> Refresh
        </button>
      </header>

      {/* New leads attention banner */}
      {newCount > 0 && (
        <button
          type="button"
          onClick={onOpenLeads}
          data-testid="overview-new-leads-banner"
          className="w-full text-left bg-[#99582A] text-white p-5 md:p-6 flex items-center justify-between gap-4 hover:bg-[#7c4620] transition-colors"
        >
          <span className="flex items-center gap-4">
            <Inbox size={26} />
            <span>
              <span className="font-display text-2xl">{newCount} new lead{newCount === 1 ? "" : "s"} need attention</span>
              <span className="block text-sm text-white/80 mt-0.5">Tap to review and start following up.</span>
            </span>
          </span>
          <ArrowLeft size={18} className="rotate-180 shrink-0" />
        </button>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard testid="stat-new" label="New Leads" value={bs.new || 0} accent="text-[#99582A]" onClick={onOpenLeads} />
        <StatCard testid="stat-contacted" label="Contacted" value={bs.contacted || 0} onClick={onOpenLeads} />
        <StatCard testid="stat-estimate-scheduled" label="Estimates Scheduled" value={bs.estimate_scheduled || 0} onClick={onOpenLeads} />
        <StatCard testid="stat-estimate-sent" label="Estimates Sent" value={bs.estimate_sent || 0} onClick={onOpenLeads} />
        <StatCard testid="stat-follow-up" label="Follow-Up" value={bs.follow_up || 0} onClick={onOpenLeads} />
        <StatCard testid="stat-won" label="Won Jobs" value={bs.won || 0} accent="text-[#15803D]" onClick={onOpenLeads} />
      </div>

      {/* Newest leads quick list */}
      <div className="bg-white border border-[#E7E2D9] p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl tracking-tight">Newest leads</h3>
          <button onClick={onOpenLeads} data-testid="overview-view-all-leads" className="text-sm font-semibold text-[#99582A] hover:text-[#111]">View all leads →</button>
        </div>
        {recent.length === 0 ? (
          <p className="py-8 text-center text-[#666] text-sm">No leads yet. Submissions from the website will appear here automatically.</p>
        ) : (
          <ul className="divide-y divide-[#F1EBE3]">
            {(newLeads.length > 0 ? newLeads : recent).slice(0, 6).map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  onClick={() => setSelected(l)}
                  data-testid={`overview-lead-${l.id}`}
                  className="w-full text-left py-3 flex items-center justify-between gap-3 hover:bg-[#FBF8F3] px-2 -mx-2"
                >
                  <span className="min-w-0">
                    <span className="font-medium block truncate">{l.name || "Unnamed lead"}</span>
                    <span className="text-xs text-[#666] block truncate">{l.project_type || l.project_description || "—"} · {l.city || l.zip || "location n/a"}</span>
                  </span>
                  <span className="flex items-center gap-3 shrink-0">
                    <StatusBadge status={l.status} />
                    <span className="text-xs text-[#888] hidden sm:inline whitespace-nowrap">{fmtDate(l.created_at)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} onSaved={load} />}
    </section>
  );
}

// ==================== QUICK LINKS (private owner shortcuts) ====================
function QuickLinksPanel({ onEdit }) {
  const [social, setSocial] = useState(null);
  useEffect(() => { (async () => setSocial((await getAllContent()).social || {}))(); }, []);

  if (!social) return <div className="p-10 text-center" data-testid="admin-quicklinks-panel"><Loader2 className="animate-spin inline" size={16}/></div>;

  const siteUrl = typeof window !== "undefined" ? window.location.origin : "/";
  const clean = (u) => (typeof u === "string" ? u.replace(/&amp;/g, "&").trim() : "");

  const cards = [
    { id: "website", label: "Zarco's Website", sub: "View the live public site", href: siteUrl, icon: <Globe size={22} /> },
    ...PUBLIC_PLATFORMS.map((p) => ({
      id: p.id,
      label: p.id === "google_business" ? "Google Reviews" : p.label,
      sub: p.id === "google_business" ? "Business profile & reviews" : `Open Zarco's ${p.label}`,
      href: clean(social[p.id]),
      icon: <BrandIcon id={p.id} size={22} />,
    })),
    { id: "timbertech_onboard", label: "TimberTech OnBoard", sub: "Contractor pro portal", href: clean(social.timbertech_onboard), icon: <ExternalLink size={22} /> },
  ].filter((c) => c.href);

  return (
    <section data-testid="admin-quicklinks-panel" className="bg-white border border-[#E7E2D9] p-5 md:p-8">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Owner shortcuts</p>
          <h2 className="font-display text-3xl tracking-tight mt-1">Quick Links</h2>
          <p className="text-sm text-[#666] mt-1">Your everyday links — tap to open in a new tab. Private to you.</p>
        </div>
        <button onClick={onEdit} data-testid="quicklinks-edit-btn" className="border border-[#E7E2D9] hover:border-[#99582A] px-3 py-2 text-sm">
          Edit links →
        </button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cards.map((c) => (
          <a
            key={c.id}
            href={c.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`quicklink-${c.id}`}
            className="group flex items-center gap-4 border border-[#E7E2D9] p-5 min-h-[72px] hover:border-[#99582A] hover:bg-[#FBF8F3] transition-colors active:bg-[#F1EBE3]"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#111] text-white shrink-0 group-hover:bg-[#99582A] transition-colors">
              {c.icon}
            </span>
            <span className="min-w-0">
              <span className="font-semibold block">{c.label}</span>
              <span className="text-xs text-[#666] block truncate">{c.sub}</span>
            </span>
            <ExternalLink size={16} className="ml-auto text-[#999] shrink-0" />
          </a>
        ))}
      </div>
    </section>
  );
}

// ==================== LEADS ====================
function LeadsPanel() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setLeads(await listLeads(showArchived)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = leads.filter((l) => {
      if (statusFilter !== "all" && normStatus(l.status) !== statusFilter) return false;
      if (!q) return true;
      return [l.name, l.phone, l.email, l.city, l.zip, l.project_type, l.project_description]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
    out = [...out].sort((a, b) => {
      const da = a.created_at || "", db = b.created_at || "";
      return sort === "newest" ? (da < db ? 1 : -1) : (da > db ? 1 : -1);
    });
    return out;
  }, [leads, query, statusFilter, sort]);

  return (
    <section data-testid="admin-leads-panel" className="bg-white border border-[#E7E2D9] p-5 md:p-8">
      <header className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Lead pipeline</p>
          <h2 className="font-display text-3xl tracking-tight mt-1">{showArchived ? "Archived leads" : "All leads"}</h2>
          <p className="text-sm text-[#666] mt-1" data-testid="leads-count">{filtered.length} shown · {leads.length} {showArchived ? "archived" : "active"}</p>
        </div>
        <button onClick={load} className="border border-[#E7E2D9] px-3 py-2 text-sm flex items-center gap-2 hover:border-[#99582A]">
          <RefreshCw size={14} /> Refresh
        </button>
      </header>

      {/* Toolbar: search / filter / sort / archived */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#999]" />
          <input
            data-testid="leads-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, phone, email, city…"
            className="w-full border border-[#E7E2D9] focus:border-[#99582A] pl-9 pr-3 py-2.5 text-sm outline-none"
          />
        </div>
        <select data-testid="leads-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-[#E7E2D9] focus:border-[#99582A] px-3 py-2.5 text-sm outline-none">
          <option value="all">All statuses</option>
          {PIPELINE.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          <option value="incomplete">Incomplete</option>
        </select>
        <button data-testid="leads-sort-toggle" onClick={() => setSort(sort === "newest" ? "oldest" : "newest")}
          className="border border-[#E7E2D9] px-3 py-2.5 text-sm flex items-center gap-2 hover:border-[#99582A]">
          <ArrowUpDown size={14} /> {sort === "newest" ? "Newest" : "Oldest"}
        </button>
        <button data-testid="leads-archived-toggle" onClick={() => setShowArchived(!showArchived)}
          className={`border px-3 py-2.5 text-sm flex items-center gap-2 ${showArchived ? "bg-[#111] text-white border-[#111]" : "border-[#E7E2D9] hover:border-[#99582A]"}`}>
          <Archive size={14} /> {showArchived ? "Viewing archived" : "Archived"}
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-[#666]"><Loader2 className="animate-spin inline mr-2" size={16}/> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-[#666] text-sm">{leads.length === 0 ? (showArchived ? "No archived leads." : "No leads yet.") : "No leads match your search/filter."}</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm">
              <thead className="text-left uppercase text-[10px] tracking-[0.2em] text-[#666] border-b border-[#E7E2D9]">
                <tr>
                  <th className="py-3 pr-3">When</th>
                  <th className="py-3 pr-3">Name</th>
                  <th className="py-3 pr-3">Source</th>
                  <th className="py-3 pr-3">Phone</th>
                  <th className="py-3 pr-3">City</th>
                  <th className="py-3 pr-3">Project</th>
                  <th className="py-3 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`}
                      className="border-b border-[#F1EBE3] hover:bg-[#FBF8F3] cursor-pointer"
                      onClick={() => setSelected(l)}>
                    <td className="py-3 pr-3 whitespace-nowrap text-[#666]">{fmtDate(l.created_at)}</td>
                    <td className="py-3 pr-3 font-medium">
                      {l.name || "—"}
                      {l.financing_interest && (
                        <span data-testid={`lead-financing-badge-${l.id}`} className="ml-2 inline-block bg-[#C27A3E] text-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide align-middle">Financing</span>
                      )}
                    </td>
                    <td className="py-3 pr-3 whitespace-nowrap"><SourceBadge source={l.source} testid={`lead-source-${l.id}`} /></td>
                    <td className="py-3 pr-3 whitespace-nowrap">{l.phone || "—"}</td>
                    <td className="py-3 pr-3">{l.city || l.zip || "—"}</td>
                    <td className="py-3 pr-3 max-w-[220px] truncate" title={l.project_type || l.project_description || ""}>
                      {l.project_type || (l.project_description ? `${l.project_description.slice(0, 48)}${l.project_description.length > 48 ? "…" : ""}` : "—")}
                    </td>
                    <td className="py-3 pr-3"><StatusBadge status={l.status} testid={`lead-status-${l.id}`} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="md:hidden space-y-3">
            {filtered.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  onClick={() => setSelected(l)}
                  data-testid={`lead-card-${l.id}`}
                  className="w-full text-left border border-[#E7E2D9] p-4 hover:border-[#99582A] transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-medium">{l.name || "Unnamed lead"}</span>
                    <StatusBadge status={l.status} />
                  </div>
                  <p className="text-xs text-[#666] mt-1">{l.project_type || l.project_description || "—"}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-[#666]">
                    <SourceBadge source={l.source} />
                    {l.phone && <span className="flex items-center gap-1"><Phone size={12}/> {l.phone}</span>}
                    <span>{l.city || l.zip || "location n/a"}</span>
                    <span className="text-[#999]">{fmtDate(l.created_at)}</span>
                    {l.financing_interest && <span className="bg-[#C27A3E] text-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Financing</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {selected && <LeadDrawer lead={selected} onClose={() => setSelected(null)} onSaved={load} />}
    </section>
  );
}

function LeadDrawer({ lead, onClose, onSaved }) {
  const [status, setStatus] = useState(normStatus(lead.status));
  const [notes, setNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const isArchived = !!lead.archived;

  const save = async () => {
    setSaving(true);
    try {
      await updateLead(lead.id, { status, notes });
      toast.success("Lead updated");
      onSaved();
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const toggleArchive = async () => {
    setBusy(true);
    try {
      await archiveLead(lead.id, !isArchived);
      toast.success(isArchived ? "Lead restored" : "Lead archived");
      onClose();
      onSaved();
    } catch { toast.error(isArchived ? "Restore failed" : "Archive failed"); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`PERMANENTLY delete this lead (${lead.name || lead.id.slice(0, 8)})? This cannot be undone. Consider archiving instead.`)) return;
    setBusy(true);
    try {
      await deleteLead(lead.id);
      toast.success("Lead deleted");
      onClose();
      onSaved();
    } catch { toast.error("Delete failed"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="lead-drawer">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Lead #{lead.id.slice(0,8)}</p>
            <h3 className="font-display text-2xl mt-1">{lead.name || "Unnamed"}</h3>
            <p className="text-sm text-[#666] flex items-center gap-2 mt-1">{fmtDate(lead.created_at)} · <StatusBadge status={lead.status} />{isArchived && <span className="bg-[#111] text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Archived</span>}</p>
          </div>
          <button onClick={onClose} data-testid="lead-drawer-close" className="text-[#666] text-xl leading-none">✕</button>
        </div>

        {/* Quick actions */}
        <div className="mt-4 flex gap-3">
          {lead.phone && <a href={`tel:${lead.phone}`} data-testid="lead-call-btn" className="flex-1 bg-[#111] text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#99582A]"><Phone size={15}/> Call</a>}
          {lead.email && <a href={`mailto:${lead.email}`} data-testid="lead-email-btn" className="flex-1 border border-[#111] py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#111] hover:text-white"><Mail size={15}/> Email</a>}
        </div>

        <div className="mt-6 space-y-3 text-sm">
          {lead.phone && <Row label="Phone"><a href={`tel:${lead.phone}`} className="text-[#99582A] font-semibold">{lead.phone}</a><CopyBtn text={lead.phone}/></Row>}
          {lead.email && <Row label="Email"><a href={`mailto:${lead.email}`} className="text-[#99582A]">{lead.email}</a><CopyBtn text={lead.email}/></Row>}
          {lead.city && <Row label="City">{lead.city}</Row>}
          {lead.address && <Row label="Address">{lead.address}</Row>}
          {lead.zip && <Row label="ZIP">{lead.zip}</Row>}
          {lead.source && <Row label="Source">{String(lead.source).replace(/_/g, " ")}</Row>}
          {lead.project_type && <Row label="Project">{lead.project_type}</Row>}
          {lead.existing_or_new && <Row label="Existing/New">{lead.existing_or_new}</Row>}
          {lead.approx_size && <Row label="Size">{lead.approx_size}</Row>}
          {lead.material_preference && <Row label="Material">{lead.material_preference}</Row>}
          {lead.railing_preference && <Row label="Railing">{lead.railing_preference}</Row>}
          {lead.covered_or_screened && <Row label="Covered/Screened">{lead.covered_or_screened}</Row>}
          {lead.timeline && <Row label="Timeline">{lead.timeline}</Row>}
          {lead.budget && <Row label="Budget">{lead.budget}</Row>}
          {lead.financing_interest != null && <Row label="Financing">{String(lead.financing_interest)}</Row>}
          {Array.isArray(lead.photos) && lead.photos.length > 0 && (
            <Row label="Photos">
              <span className="flex flex-wrap gap-2">
                {lead.photos.map((u, i) => (
                  <a key={u} href={u} target="_blank" rel="noreferrer" data-testid={`lead-photo-${i}`} className="block w-16 h-16 border border-[#E7E2D9] overflow-hidden hover:border-[#99582A]">
                    <img src={u} alt={`Homeowner upload ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </a>
                ))}
              </span>
            </Row>
          )}
          {lead.contact_pref && <Row label="Contact preference">{lead.contact_pref}</Row>}
          {lead.project_description && <Row label="Description">{lead.project_description}</Row>}
          {(lead.page || lead.landing_page || lead.referrer || lead.utm_source || lead.utm_medium || lead.utm_campaign || lead.gclid) && (
            <div className="pt-3 border-t border-[#E7E2D9]" data-testid="lead-attribution">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#99582A] font-semibold pb-2">Lead source</p>
              <div className="space-y-3">
                {lead.page && <Row label="Submitted from">{lead.page}</Row>}
                {lead.landing_page && <Row label="Landing page">{lead.landing_page}</Row>}
                {lead.referrer && <Row label="Referrer"><span className="break-all">{lead.referrer}</span></Row>}
                {lead.utm_source && <Row label="UTM source">{lead.utm_source}</Row>}
                {lead.utm_medium && <Row label="UTM medium">{lead.utm_medium}</Row>}
                {lead.utm_campaign && <Row label="UTM campaign">{lead.utm_campaign}</Row>}
                {lead.gclid && <Row label="Google click ID"><span className="break-all">{lead.gclid}</span></Row>}
              </div>
            </div>
          )}
          {lead.ai_summary && (
            <div className="pt-3 border-t border-[#E7E2D9]">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#99582A] font-semibold">AI conversation summary</p>
              <p className="mt-2 text-sm text-[#4A4A4A] italic">{lead.ai_summary}</p>
            </div>
          )}
          {lead.delivery && (
            <div className="pt-3 border-t border-[#E7E2D9] text-xs space-y-2" data-testid="lead-delivery-status">
              {[
                ["Email to Zarco's", lead.delivery.email],
                ["SMS to Zarco's", lead.delivery.sms],
                ["Customer confirmation", lead.delivery.customer_email],
              ].map(([label, d]) => (
                <p key={label} className="flex items-center gap-2 flex-wrap">
                  <b className="text-[#666]">{label}:</b>
                  {d?.ok ? (
                    <span className="bg-green-100 text-green-800 px-2 py-0.5 font-semibold">Sent ✓</span>
                  ) : (
                    <span className="bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold">
                      Not sent — {d?.reason || d?.status || "provider not configured"}
                    </span>
                  )}
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 border-t border-[#E7E2D9] pt-6">
          <label className="block text-[11px] uppercase tracking-[0.2em] font-semibold">Status</label>
          <select data-testid="lead-status-select" className="mt-2 w-full border border-[#E7E2D9] p-3 text-sm" value={status} onChange={(e)=>setStatus(e.target.value)}>
            {PIPELINE.map((p)=>(<option key={p.id} value={p.id}>{p.label}</option>))}
            {normStatus(lead.status) === "incomplete" && <option value="incomplete">Incomplete</option>}
          </select>
          <label className="block mt-4 text-[11px] uppercase tracking-[0.2em] font-semibold">Private notes (internal only)</label>
          <textarea rows={4} value={notes} onChange={(e)=>setNotes(e.target.value)} data-testid="lead-notes" className="mt-2 w-full border border-[#E7E2D9] p-3 text-sm" placeholder="Call outcomes, site visit details, reminders…"/>
          <button disabled={saving} onClick={save} data-testid="lead-save-btn" className="mt-4 w-full bg-[#111] text-white py-3 font-semibold hover:bg-[#99582A] disabled:opacity-50">Save changes</button>

          <div className="mt-6 pt-5 border-t border-[#F1EBE3] flex items-center justify-between gap-3">
            <button disabled={busy} onClick={toggleArchive} data-testid="lead-archive-btn"
              className="flex-1 border border-[#111] text-[#111] py-2.5 text-sm font-semibold hover:bg-[#111] hover:text-white disabled:opacity-50 flex items-center justify-center gap-2">
              {isArchived ? <><ArchiveRestore size={15}/> Restore lead</> : <><Archive size={15}/> Archive lead</>}
            </button>
            <button disabled={busy} onClick={remove} data-testid="lead-delete-btn"
              className="text-xs text-red-600 hover:underline shrink-0 flex items-center gap-1">
              <Trash2 size={13}/> Delete
            </button>
          </div>
          <p className="mt-2 text-[11px] text-[#999]">Archiving hides the lead but keeps the record. Delete is permanent.</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return <div className="flex gap-3 items-start"><span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#666] w-32 shrink-0 pt-0.5">{label}</span><div className="flex-1 flex items-center gap-2">{children}</div></div>;
}
function CopyBtn({ text }) {
  return <button onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }} className="text-[#666] hover:text-[#99582A]"><Copy size={13}/></button>;
}
function fmtDate(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso || ""; } }

// ==================== HERO (image + copy) ====================
function HeroPanel() {
  const [hero, setHero] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => setHero((await getAllContent()).hero || {}))(); }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const { url } = await uploadMedia(file);
      setHero({ ...hero, image: url });
      toast.success("Uploaded — click Save to apply.");
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try { await setContent("hero", hero); toast.success("Homepage hero updated"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!hero) return <div className="p-10 text-center"><Loader2 className="animate-spin inline" size={16}/></div>;

  return (
    <section data-testid="admin-hero-panel" className="bg-white border border-[#E7E2D9] p-6 md:p-10">
      <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Homepage</p>
      <h2 className="font-display text-3xl tracking-tight mt-1">Hero image & copy</h2>
      <p className="text-sm text-[#666] mt-2 max-w-2xl">
        Replace the homepage hero photograph and headline. Uploaded images are stored persistently and served directly from the site — no rebuild needed.
      </p>

      <div className="grid md:grid-cols-2 gap-8 mt-8">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.2em] font-semibold">Current image</label>
          <div className="mt-2 aspect-[16/10] bg-[#111] overflow-hidden">
            {hero.image && <img src={hero.image} alt="Hero preview" className="w-full h-full object-cover"/>}
          </div>
          <label className="mt-4 flex items-center justify-center gap-3 border-2 border-dashed border-[#E7E2D9] hover:border-[#99582A] cursor-pointer py-6 transition-colors">
            {uploading ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
            <span className="text-sm font-medium">Upload new photo</span>
            <input type="file" accept="image/*" data-testid="hero-upload" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
          </label>
          <input type="text" value={hero.image || ""} onChange={(e)=>setHero({...hero, image:e.target.value})} className="mt-3 w-full border-b border-[#E7E2D9] py-2 text-xs font-mono" placeholder="/api/media/..."/>
        </div>

        <div className="space-y-5">
          <Field label="Overline" value={hero.overline} onChange={(v)=>setHero({...hero, overline:v})}/>
          <Field label="Headline" value={hero.headline} onChange={(v)=>setHero({...hero, headline:v})}/>
          <Field label="Subheadline" value={hero.subheadline} onChange={(v)=>setHero({...hero, subheadline:v})} textarea/>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Primary CTA label" value={hero.cta_primary_label} onChange={(v)=>setHero({...hero, cta_primary_label:v})}/>
            <Field label="Primary CTA link" value={hero.cta_primary_href} onChange={(v)=>setHero({...hero, cta_primary_href:v})}/>
            <Field label="Secondary CTA label" value={hero.cta_secondary_label} onChange={(v)=>setHero({...hero, cta_secondary_label:v})}/>
            <Field label="Secondary CTA link" value={hero.cta_secondary_href} onChange={(v)=>setHero({...hero, cta_secondary_href:v})}/>
          </div>
        </div>
      </div>

      <button disabled={saving} onClick={save} data-testid="hero-save-btn" className="mt-8 bg-[#111] text-white px-8 py-4 font-semibold hover:bg-[#99582A] disabled:opacity-50">
        {saving ? "Saving…" : "Save homepage hero"}
      </button>
    </section>
  );
}

// ==================== CONTACT / FINANCING CONTENT ====================
function ContentPanel() {
  const [contact, setContact] = useState(null);
  const [fin, setFin] = useState(null);
  const [rev, setRev] = useState(null);
  const [social, setSocial] = useState(null);
  const [projJson, setProjJson] = useState("");
  const [projErr, setProjErr] = useState("");
  const [faqJson, setFaqJson] = useState("");
  const [faqErr, setFaqErr] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => {
    const c = await getAllContent();
    setContact(c.contact || {}); setFin(c.financing || {}); setRev(c.reviews || {}); setSocial(c.social || {});
    setProjJson(JSON.stringify(c.projects || { items: [] }, null, 2));
    setFaqJson(JSON.stringify(c.home_faq || { items: [] }, null, 2));
  })(); }, []);
  const save = async () => {
    setSaving(true);
    setProjErr("");
    setFaqErr("");
    let projects;
    try {
      projects = JSON.parse(projJson);
      if (!Array.isArray(projects.items)) throw new Error("Projects must contain an items array");
    } catch (e) {
      setProjErr(`Invalid JSON: ${e.message}`);
      setSaving(false);
      return;
    }
    let faq;
    try {
      faq = JSON.parse(faqJson);
      if (!Array.isArray(faq.items)) throw new Error("FAQ must contain an items array");
      for (const it of faq.items) {
        if (!it.q || !it.a) throw new Error("Every FAQ item needs q and a fields");
      }
    } catch (e) {
      setFaqErr(`Invalid JSON: ${e.message}`);
      setSaving(false);
      return;
    }
    try {
      await setContent("contact", contact);
      await setContent("financing", fin);
      await setContent("reviews", rev);
      await setContent("social", social);
      await setContent("projects", projects);
      await setContent("home_faq", faq);
      toast.success("Content saved");
    }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  if (!contact || !fin || !rev || !social) return <div className="p-10 text-center"><Loader2 className="animate-spin inline" size={16}/></div>;
  return (
    <section data-testid="admin-content-panel" className="bg-white border border-[#E7E2D9] p-6 md:p-10 space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Contact info</p>
        <h2 className="font-display text-3xl tracking-tight mt-1">Business contact</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-5">
          <Field label="Phone (call or text)" value={contact.phone_main} onChange={(v)=>setContact({...contact, phone_main:v})}/>
          <Field label="Email" value={contact.email} onChange={(v)=>setContact({...contact, email:v})}/>
          <Field label="Service area (text)" value={contact.service_area} onChange={(v)=>setContact({...contact, service_area:v})}/>
        </div>
      </div>
      <div className="pt-6 border-t border-[#E7E2D9]">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Google reviews</p>
        <div className="mt-4 flex items-center gap-3">
          <label className="text-sm inline-flex items-center gap-2">
            <input type="checkbox" checked={!!rev.enabled} data-testid="reviews-enabled-toggle" onChange={(e)=>setRev({...rev, enabled:e.target.checked})}/>
            Show reviews section on homepage
          </label>
        </div>
        <div className="grid md:grid-cols-2 gap-5 mt-4">
          <Field label="Google Business Profile reviews URL" value={rev.gbp_url} onChange={(v)=>setRev({...rev, gbp_url:v})}/>
          <Field label="Section heading" value={rev.heading} onChange={(v)=>setRev({...rev, heading:v})}/>
        </div>
        <p className="mt-2 text-xs text-[#666]">
          Only enable once the Google Business Profile link is correct. Individual review cards can be added later — never paste unverified reviews.
        </p>
      </div>
      <div className="pt-6 border-t border-[#E7E2D9]">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Social &amp; links</p>
        <p className="mt-2 text-xs text-[#666] max-w-2xl">
          Paste the full URL for each account. A platform only appears on the public site (footer + gallery)
          when it has a URL — leave a field blank to hide it. TimberTech OnBoard is private (Quick Links only, never public).
        </p>
        <div className="grid md:grid-cols-2 gap-5 mt-4">
          <Field label="Instagram URL" testId="social-instagram" value={social.instagram} onChange={(v)=>setSocial({...social, instagram:v})}/>
          <Field label="Facebook URL" testId="social-facebook" value={social.facebook} onChange={(v)=>setSocial({...social, facebook:v})}/>
          <Field label="TikTok URL" testId="social-tiktok" value={social.tiktok} onChange={(v)=>setSocial({...social, tiktok:v})}/>
          <Field label="YouTube URL" testId="social-youtube" value={social.youtube} onChange={(v)=>setSocial({...social, youtube:v})}/>
          <Field label="Google Business Profile / Reviews URL" testId="social-google" value={social.google_business} onChange={(v)=>setSocial({...social, google_business:v})}/>
          <Field label="TimberTech OnBoard URL (private)" testId="social-onboard" value={social.timbertech_onboard} onChange={(v)=>setSocial({...social, timbertech_onboard:v})}/>
        </div>
        <div className="mt-4">
          <label className="block">
            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">Gallery "See More of Our Work" button points to</span>
            <select data-testid="social-gallery-cta" value={social.gallery_cta_platform || "instagram"} onChange={(e)=>setSocial({...social, gallery_cta_platform:e.target.value})}
              className="mt-2 w-full md:w-64 border border-[#E7E2D9] focus:border-[#99582A] p-2.5 text-sm outline-none">
              {PUBLIC_PLATFORMS.map((p)=>(<option key={p.id} value={p.id}>{p.id === "google_business" ? "Google" : p.label}</option>))}
            </select>
          </label>
        </div>
      </div>
      <div className="pt-6 border-t border-[#E7E2D9]">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Financing messaging</p>
        <div className="mt-4 flex items-center gap-3">
          <label className="text-sm inline-flex items-center gap-2">
            <input type="checkbox" checked={!!fin.enabled} onChange={(e)=>setFin({...fin, enabled:e.target.checked})}/>
            Show financing note on site
          </label>
        </div>
        <Field label="Message" value={fin.message} onChange={(v)=>setFin({...fin, message:v})} textarea/>
        <div className="mt-6 pt-6 border-t border-[#F1EBE3]">
          <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Financing provider (future application link)</p>
          <div className="grid md:grid-cols-2 gap-5 mt-4">
            <Field label="Provider name" testId="financing-provider-name" value={fin.provider_name||""} onChange={(v)=>setFin({...fin, provider_name:v})}/>
            <Field label="Provider URL (approved personalized link)" testId="financing-provider-url" value={fin.provider_url||""} onChange={(v)=>setFin({...fin, provider_url:v})}/>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <label className="text-sm inline-flex items-center gap-2">
              <input type="checkbox" checked={!!fin.provider_active} data-testid="financing-provider-active-toggle" onChange={(e)=>setFin({...fin, provider_active:e.target.checked})}/>
              Activate provider CTA ("Check Financing Options") on /financing
            </label>
          </div>
          <Field label="Provider disclosure (shown when active)" testId="financing-provider-disclosure" value={fin.provider_disclosure||""} onChange={(v)=>setFin({...fin, provider_disclosure:v})} textarea/>
          <Field label="Provider approved logo URL (optional)" testId="financing-provider-logo" value={fin.provider_logo||""} onChange={(v)=>setFin({...fin, provider_logo:v})}/>
          <p className="mt-2 text-xs text-[#666]">
            Activate ONLY after Zarco's receives the approved personalized contractor link (e.g. Acorn Finance). When active, a "Check Financing Options" button appears on /financing and opens this URL in a new tab. The page's estimate and financing-interest flow stays regardless.
          </p>
        </div>
      </div>
      <div className="pt-6 border-t border-[#E7E2D9]">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Project pages (advanced)</p>
        <p className="mt-2 text-xs text-[#666] max-w-2xl">
          JSON list powering /projects/[id] detail pages and homepage project cards. Fields per project: id, title, city, project_type, material, color, railing, stairs, covered, description, photos[].
          Only add real, known project details.
        </p>
        <textarea
          value={projJson}
          onChange={(e)=>setProjJson(e.target.value)}
          rows={8}
          spellCheck={false}
          data-testid="projects-json"
          className="mt-3 w-full border border-[#E7E2D9] focus:border-[#99582A] p-3 font-mono text-xs outline-none"
        />
        {projErr && <p className="text-xs text-red-600" data-testid="projects-json-error">{projErr}</p>}
      </div>
      <div className="pt-6 border-t border-[#E7E2D9]">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Homepage FAQ (advanced)</p>
        <p className="mt-2 text-xs text-[#666] max-w-2xl">
          JSON list powering the homepage "Frequently Asked Questions" accordion. Fields per item: q, a, optional note, optional cta_label + cta_href, or cta_kind "financing" (CTA switches automatically when a financing provider is activated).
        </p>
        <textarea
          value={faqJson}
          onChange={(e)=>setFaqJson(e.target.value)}
          rows={10}
          spellCheck={false}
          data-testid="home-faq-json"
          className="mt-3 w-full border border-[#E7E2D9] focus:border-[#99582A] p-3 font-mono text-xs outline-none"
        />
        {faqErr && <p className="text-xs text-red-600" data-testid="home-faq-json-error">{faqErr}</p>}
      </div>
      <button onClick={save} disabled={saving} data-testid="content-save-btn" className="bg-[#111] text-white px-8 py-4 font-semibold hover:bg-[#99582A] disabled:opacity-50">
        {saving ? "Saving…" : "Save content"}
      </button>
    </section>
  );
}

// ==================== AI KNOWLEDGE ====================
function AIKnowledgePanel() {
  const [k, setK] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => setK((await getAllContent()).ai_knowledge || {}))(); }, []);
  const save = async () => {
    setSaving(true);
    try { await setContent("ai_knowledge", k); toast.success("AI knowledge updated"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  if (!k) return <div className="p-10 text-center"><Loader2 className="animate-spin inline" size={16}/></div>;
  return (
    <section data-testid="admin-ai-panel" className="bg-white border border-[#E7E2D9] p-6 md:p-10 space-y-5">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">AI Assistant</p>
        <h2 className="font-display text-3xl tracking-tight mt-1">Approved knowledge</h2>
        <p className="text-sm text-[#666] mt-2 max-w-2xl">
          What the AI is allowed to say. The assistant is instructed to never invent prices, warranties, availability, financing terms, certifications or promises. Only edit these blocks.
        </p>
      </div>
      <Field label="Instructions" value={k.instructions} onChange={(v)=>setK({...k, instructions:v})} textarea/>
      <Field label="Services offered" value={k.services} onChange={(v)=>setK({...k, services:v})} textarea/>
      <Field label="Service area" value={k.service_area} onChange={(v)=>setK({...k, service_area:v})} textarea/>
      <Field label="Process" value={k.process} onChange={(v)=>setK({...k, process:v})} textarea/>
      <Field label="Guardrails" value={k.guardrails} onChange={(v)=>setK({...k, guardrails:v})} textarea/>
      <button onClick={save} disabled={saving} className="bg-[#111] text-white px-8 py-4 font-semibold hover:bg-[#99582A] disabled:opacity-50">
        {saving ? "Saving…" : "Save AI knowledge"}
      </button>
    </section>
  );
}

// ==================== MEDIA LIBRARY ====================
function MediaPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = async () => { setLoading(true); try { setItems(await listMedia()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of files) await uploadMedia(f);
      toast.success("Uploaded");
      await load();
    } catch { toast.error("Upload failed"); } finally { setUploading(false); if (fileRef.current) fileRef.current.value=""; }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try { await deleteMedia(id); await load(); } catch { toast.error("Delete failed"); }
  };
  return (
    <section data-testid="admin-media-panel" className="bg-white border border-[#E7E2D9] p-6 md:p-10">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Media library</p>
          <h2 className="font-display text-3xl tracking-tight mt-1">Uploaded photos</h2>
        </div>
        <label className="border border-[#E7E2D9] hover:border-[#99582A] px-4 py-2 text-sm cursor-pointer inline-flex items-center gap-2">
          {uploading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>} Upload
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="media-upload-input" onChange={(e)=>upload(Array.from(e.target.files||[]))} />
        </label>
      </div>
      {loading ? <div className="py-10 text-center"><Loader2 className="animate-spin inline" size={16}/></div>
        : items.length===0 ? <div className="py-10 text-center text-[#666]">No uploads yet.</div>
        : (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {items.map((m) => (
              <div key={m.id} data-testid={`media-item-${m.id}`} className="group relative border border-[#E7E2D9]">
                <img src={`/api/media/${m.id}`} alt={m.filename} className="w-full aspect-square object-cover"/>
                <div className="p-2 text-xs">
                  <p className="truncate">{m.filename}</p>
                  <p className="text-[10px] text-[#888]">{(m.size/1024).toFixed(0)} KB</p>
                  <div className="mt-2 flex gap-2">
                    <button onClick={()=>{navigator.clipboard.writeText(`/api/media/${m.id}`); toast.success("URL copied");}} className="flex-1 border border-[#E7E2D9] py-1 text-[10px] uppercase tracking-widest hover:border-[#99582A]"><Copy size={11} className="inline mr-1"/>Copy URL</button>
                    <button onClick={()=>del(m.id)} data-testid={`media-delete-${m.id}`} className="border border-[#E7E2D9] py-1 px-2 hover:border-red-500 hover:text-red-500"><Trash2 size={11}/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </section>
  );
}

// ==================== AERIAL SHOWCASE ====================
function AerialPanel() {
  const [a, setA] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  useEffect(() => { (async () => setA((await getAllContent()).aerial || {}))(); }, []);

  const save = async () => {
    setSaving(true);
    try { await setContent("aerial", a); toast.success("Aerial showcase updated"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!a) return <div className="p-10 text-center"><Loader2 className="animate-spin inline" size={16}/></div>;
  const projects = Array.isArray(a.projects) ? a.projects : [];
  const setP = (i, patch) => setA({ ...a, projects: projects.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const move = (i, dir) => {
    const arr = [...projects];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setA({ ...a, projects: arr });
  };
  const uploadFor = async (i, file, field) => {
    if (!file) return;
    setBusy(`${i}:${field}`);
    try {
      const { url } = await uploadMedia(file);
      setP(i, field === "src" ? { src: url, srcset: "" } : { poster: url });
      toast.success("Uploaded — click Save to apply.");
    } catch { toast.error("Upload failed"); } finally { setBusy(""); }
  };
  const addProject = () => setA({
    ...a,
    projects: [...projects, {
      id: `p-${Date.now()}`, kind: "image", src: "", srcset: "", title: "New Project",
      location: "", project_type: "", brand: "", description: "", poster: "", alt: "",
      featured: false, visible: false,
    }],
  });

  return (
    <section data-testid="admin-aerial-panel" className="bg-white border border-[#E7E2D9] p-6 md:p-10 space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Gallery → Aerial section</p>
        <h2 className="font-display text-3xl tracking-tight mt-1">Aerial project showcase</h2>
        <p className="text-sm text-[#666] mt-2 max-w-2xl">
          Manage the aerial/drone section end-to-end: upload or replace images & video, pick posters,
          edit captions, reorder, hide/show, and mark one project Featured. Hidden or empty projects
          never appear on the public site — no placeholder boxes are ever shown.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Eyebrow text" value={a.overline} onChange={(v)=>setA({...a, overline:v})}/>
        <Field label="Section heading" value={a.heading} onChange={(v)=>setA({...a, heading:v})}/>
      </div>
      <Field label="Supporting copy" value={a.subcopy} onChange={(v)=>setA({...a, subcopy:v})} textarea/>
      <div className="border border-[#C27A3E]/40 bg-[#FBF8F3] p-5">
        <Field
          label="Verified credential / badge (leave EMPTY until officially earned & approved)"
          value={a.credential_badge}
          onChange={(v)=>setA({...a, credential_badge:v})}
        />
        <p className="mt-2 text-xs text-[#666]">
          Renders as a small badge under the section heading only while filled. Keep empty — no credential is implied while blank.
        </p>
      </div>

      <div className="pt-6 border-t border-[#E7E2D9] space-y-6">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Projects ({projects.length})</p>
        {projects.map((p, i) => (
          <div key={p.id} data-testid={`aerial-editor-${p.id}`} className="border border-[#E7E2D9] p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 text-sm">
                <span className="font-display text-lg text-[#99582A]">{String(i + 1).padStart(2, "0")}</span>
                <span className="uppercase text-[10px] tracking-[0.2em] font-semibold border px-2 py-0.5">{p.kind}</span>
                {p.featured && <span className="uppercase text-[10px] tracking-[0.2em] font-semibold bg-[#111] text-white px-2 py-0.5">Featured</span>}
                {!p.visible && <span className="uppercase text-[10px] tracking-[0.2em] font-semibold bg-amber-100 text-amber-800 px-2 py-0.5">Hidden</span>}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={()=>move(i,-1)} disabled={i===0} className="border border-[#E7E2D9] px-3 py-1.5 text-xs disabled:opacity-40">↑ Up</button>
                <button onClick={()=>move(i,1)} disabled={i===projects.length-1} className="border border-[#E7E2D9] px-3 py-1.5 text-xs disabled:opacity-40">↓ Down</button>
                <button onClick={()=>{ if(window.confirm("Remove this project from the section?")) setA({...a, projects: projects.filter((_,j)=>j!==i)}); }}
                  data-testid={`aerial-remove-${p.id}`}
                  className="border border-red-300 text-red-600 px-3 py-1.5 text-xs hover:bg-red-50">Remove</button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              <div>
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">Media</span>
                <div className="mt-2 aspect-[4/3] bg-[#111] overflow-hidden flex items-center justify-center">
                  {p.src
                    ? (p.kind === "video"
                        ? <video src={p.src} poster={p.poster||undefined} className="w-full h-full object-cover" muted/>
                        : <img src={p.src} alt={p.alt||""} className="w-full h-full object-cover"/>)
                    : <span className="text-white/50 text-xs">No media yet</span>}
                </div>
                <label className="mt-3 flex items-center justify-center gap-2 border-2 border-dashed border-[#E7E2D9] hover:border-[#99582A] cursor-pointer py-3 text-xs font-medium transition-colors">
                  {busy===`${i}:src` ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>}
                  {p.src ? "Replace image / video" : "Upload image / video"}
                  <input type="file" accept="image/*,video/mp4,video/webm,video/quicktime" className="hidden"
                    data-testid={`aerial-upload-${p.id}`}
                    onChange={(e)=>{ const f=e.target.files?.[0]; if(f){ setP(i,{kind: f.type.startsWith("video/") ? "video" : "image"}); uploadFor(i, f, "src"); } e.target.value=""; }}/>
                </label>
                {p.kind === "video" && (
                  <label className="mt-2 flex items-center justify-center gap-2 border border-[#E7E2D9] hover:border-[#99582A] cursor-pointer py-2.5 text-xs transition-colors">
                    {busy===`${i}:poster` ? <Loader2 size={13} className="animate-spin"/> : <ImageIcon size={13}/>}
                    {p.poster ? "Replace poster" : "Upload poster / thumbnail"}
                    <input type="file" accept="image/*" className="hidden"
                      data-testid={`aerial-poster-${p.id}`}
                      onChange={(e)=>{ const f=e.target.files?.[0]; if(f) uploadFor(i, f, "poster"); e.target.value=""; }}/>
                  </label>
                )}
              </div>
              <div className="md:col-span-2 grid sm:grid-cols-2 gap-4 content-start">
                <Field label="Title" value={p.title} onChange={(v)=>setP(i,{title:v})}/>
                <Field label="Location (only if known)" value={p.location} onChange={(v)=>setP(i,{location:v})}/>
                <Field label="Project type (only if known)" value={p.project_type} onChange={(v)=>setP(i,{project_type:v})}/>
                <Field label="Decking brand / product (optional)" value={p.brand} onChange={(v)=>setP(i,{brand:v})}/>
                <div className="sm:col-span-2"><Field label="Alt text (describe only what's visible)" value={p.alt} onChange={(v)=>setP(i,{alt:v})}/></div>
                <div className="sm:col-span-2"><Field label="Description (optional)" value={p.description} onChange={(v)=>setP(i,{description:v})} textarea/></div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!p.visible} data-testid={`aerial-visible-${p.id}`}
                    onChange={(e)=>setP(i,{visible:e.target.checked})}/> Visible on site
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!p.featured} data-testid={`aerial-featured-${p.id}`}
                    onChange={(e)=> setA({...a, projects: projects.map((q,j)=>({...q, featured: j===i ? e.target.checked : (e.target.checked ? false : q.featured)}))})}/> Featured (largest slot)
                </label>
              </div>
            </div>
          </div>
        ))}
        <button onClick={addProject} data-testid="aerial-add-project-btn"
          className="border border-[#E7E2D9] hover:border-[#99582A] px-5 py-3 text-sm">
          + Add project (starts hidden until you upload media)
        </button>
      </div>

      <button onClick={save} disabled={saving} data-testid="aerial-save-btn"
        className="bg-[#111] text-white px-8 py-4 font-semibold hover:bg-[#99582A] disabled:opacity-50">
        {saving ? "Saving…" : "Save aerial showcase"}
      </button>
    </section>
  );
}

// ==================== TIMBERTECH PAGE ====================
function TimberTechPanel() {
  const [t, setT] = useState(null);
  const [catJson, setCatJson] = useState("");
  const [catErr, setCatErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingCat, setSavingCat] = useState(false);
  useEffect(() => { (async () => {
    const all = await getAllContent();
    setT(all.timbertech || {});
    if (all.timbertech_catalog) setCatJson(JSON.stringify(all.timbertech_catalog, null, 2));
  })(); }, []);

  const save = async () => {
    setSaving(true);
    try { await setContent("timbertech", t); toast.success("TimberTech page updated"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const saveCatalog = async () => {
    setCatErr("");
    let parsed;
    try {
      parsed = JSON.parse(catJson);
      if (!Array.isArray(parsed.composite) || !Array.isArray(parsed.pvc) || !Array.isArray(parsed.railing)) {
        throw new Error("Catalog must contain composite, pvc and railing arrays");
      }
    } catch (e) {
      setCatErr(`Invalid JSON: ${e.message}`);
      return;
    }
    setSavingCat(true);
    try { await setContent("timbertech_catalog", parsed); toast.success("Product catalog updated"); }
    catch { toast.error("Catalog save failed"); } finally { setSavingCat(false); }
  };

  if (!t) return <div className="p-10 text-center"><Loader2 className="animate-spin inline" size={16}/></div>;
  const faqs = Array.isArray(t.faqs) ? t.faqs : [];
  const setFaq = (i, key, v) => setT({ ...t, faqs: faqs.map((f, j) => (j === i ? { ...f, [key]: v } : f)) });

  return (
    <section data-testid="admin-timbertech-panel" className="bg-white border border-[#E7E2D9] p-6 md:p-10 space-y-8">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">/timbertech page</p>
        <h2 className="font-display text-3xl tracking-tight mt-1">TimberTech content</h2>
        <p className="text-sm text-[#666] mt-2 max-w-2xl">
          Everything on the public TimberTech page is edited here — no redesign needed as the
          manufacturer relationship grows. Only publish product specs, collection names, warranty
          numbers, or credentials once verified against current official TimberTech information.
        </p>
      </div>

      {/* Credentials — future official designation */}
      <div className="border border-[#C27A3E]/40 bg-[#FBF8F3] p-5">
        <Field
          label="Official credentials / designation (leave EMPTY until earned & approved)"
          value={t.credentials}
          onChange={(v) => setT({ ...t, credentials: v })}
        />
        <p className="mt-2 text-xs text-[#666]">
          When filled, this appears as a badge on the TimberTech page (e.g. an official TimberTech
          designation once earned). While empty, nothing is shown and no status is implied.
        </p>
        <div className="grid md:grid-cols-2 gap-5 mt-5">
          <Field
            label="Credential badge image URL (TIMBERTECH_CREDENTIAL_BADGE — leave EMPTY until the official badge asset is supplied)"
            testId="tt-credential-badge-input"
            value={t.credential_badge}
            onChange={(v) => setT({ ...t, credential_badge: v })}
          />
          <Field
            label="TimberTech profile URL (TIMBERTECH_PROFILE_URL — 'View Our TimberTech Profile' button appears only when set)"
            testId="tt-profile-url-input"
            value={t.profile_url}
            onChange={(v) => setT({ ...t, profile_url: v })}
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Overline" value={t.overline} onChange={(v) => setT({ ...t, overline: v })} />
        <Field label="Headline" value={t.headline} onChange={(v) => setT({ ...t, headline: v })} />
      </div>
      <Field label="Intro" value={t.intro} onChange={(v) => setT({ ...t, intro: v })} textarea />
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Composite card title" value={t.composite_title} onChange={(v) => setT({ ...t, composite_title: v })} />
        <Field label="Advanced PVC card title" value={t.pvc_title} onChange={(v) => setT({ ...t, pvc_title: v })} />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Composite card body" value={t.composite_body} onChange={(v) => setT({ ...t, composite_body: v })} textarea />
        <Field label="Advanced PVC card body" value={t.pvc_body} onChange={(v) => setT({ ...t, pvc_body: v })} textarea />
      </div>
      <Field label="Good / Better / Best text" value={t.good_better_best} onChange={(v) => setT({ ...t, good_better_best: v })} textarea />
      <Field label="Warranty note" value={t.warranty_note} onChange={(v) => setT({ ...t, warranty_note: v })} textarea />

      {/* FAQ editor */}
      <div className="pt-6 border-t border-[#E7E2D9] space-y-4">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Homeowner FAQs</p>
        {faqs.map((f, i) => (
          <div key={i} className="border border-[#E7E2D9] p-4 space-y-3" data-testid={`tt-faq-editor-${i}`}>
            <Field label="Question" value={f.q} onChange={(v) => setFaq(i, "q", v)} />
            <Field label="Answer" value={f.a} onChange={(v) => setFaq(i, "a", v)} textarea />
            <button
              onClick={() => setT({ ...t, faqs: faqs.filter((_, j) => j !== i) })}
              className="text-xs text-red-600 hover:underline"
            >
              Remove question
            </button>
          </div>
        ))}
        <button
          onClick={() => setT({ ...t, faqs: [...faqs, { q: "", a: "" }] })}
          data-testid="tt-faq-add-btn"
          className="border border-[#E7E2D9] hover:border-[#99582A] px-4 py-2 text-sm"
        >
          + Add FAQ
        </button>
      </div>

      {/* Product catalog (advanced JSON) */}
      <div className="pt-6 border-t border-[#E7E2D9] space-y-3">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Product catalog (colors & railing)</p>
        <p className="text-xs text-[#666] max-w-2xl">
          Advanced: the collections, colors, and railing options shown in the product explorer.
          Only edit with names verified from TimberTech's official website (timbertech.com → Products).
          JSON format — ask for help if unsure.
        </p>
        <textarea
          value={catJson}
          onChange={(e) => setCatJson(e.target.value)}
          rows={12}
          spellCheck={false}
          data-testid="tt-catalog-json"
          className="w-full border border-[#E7E2D9] focus:border-[#99582A] p-3 font-mono text-xs outline-none"
        />
        {catErr && <p className="text-xs text-red-600" data-testid="tt-catalog-error">{catErr}</p>}
        <button onClick={saveCatalog} disabled={savingCat} data-testid="tt-catalog-save-btn"
          className="border border-[#111] px-6 py-3 text-sm font-semibold hover:bg-[#111] hover:text-white disabled:opacity-50">
          {savingCat ? "Saving…" : "Save product catalog"}
        </button>
      </div>

      <button onClick={save} disabled={saving} data-testid="tt-save-btn"
        className="bg-[#111] text-white px-8 py-4 font-semibold hover:bg-[#99582A] disabled:opacity-50">
        {saving ? "Saving…" : "Save TimberTech page"}
      </button>
    </section>
  );
}

// ==================== SECURITY (password + MFA) ====================
function SecurityPanel() {
  const [info, setInfo] = useState(null);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [mfa, setMfa] = useState(null); // { secret, otpauth_url } pending setup
  const [mfaCode, setMfaCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [disablePw, setDisablePw] = useState("");

  const load = async () => { try { setInfo(await me()); } catch { /* token invalid */ } };
  useEffect(() => { load(); }, []);

  const submitPw = async (e) => {
    e.preventDefault();
    if (next !== next2) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await changePassword(cur, next);
      toast.success("Password updated");
      setCur(""); setNext(""); setNext2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update password");
    } finally { setPwBusy(false); }
  };

  const startMfa = async () => {
    setMfaBusy(true);
    try { setMfa(await mfaSetup()); } catch { toast.error("Could not start MFA setup"); } finally { setMfaBusy(false); }
  };

  const confirmMfa = async (e) => {
    e.preventDefault();
    setMfaBusy(true);
    try {
      await mfaEnable(mfaCode);
      toast.success("Two-factor authentication enabled");
      setMfa(null); setMfaCode("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally { setMfaBusy(false); }
  };

  const disableMfa = async (e) => {
    e.preventDefault();
    setMfaBusy(true);
    try {
      await mfaDisable(disablePw);
      toast.success("Two-factor authentication disabled");
      setDisablePw("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not disable MFA");
    } finally { setMfaBusy(false); }
  };

  return (
    <section data-testid="admin-security-panel" className="bg-white border border-[#E7E2D9] p-6 md:p-10 space-y-10">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Account security</p>
        <h2 className="font-display text-3xl tracking-tight mt-1">Password & two-factor</h2>
        <p className="text-sm text-[#666] mt-2 max-w-2xl">
          Signed in as <b>{info?.email || "…"}</b>. Two-factor authentication is{" "}
          <b data-testid="mfa-status">{info ? (info.mfa_enabled ? "ON" : "OFF") : "…"}</b>.
        </p>
      </div>

      {/* Change password */}
      <form onSubmit={submitPw} className="max-w-md space-y-5" data-testid="change-password-form">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Change password</p>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">Current password</span>
          <input type="password" required value={cur} onChange={(e) => setCur(e.target.value)}
            data-testid="change-password-current"
            className="mt-2 w-full border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-2 text-sm outline-none" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">New password (min 10 chars)</span>
          <input type="password" required minLength={10} value={next} onChange={(e) => setNext(e.target.value)}
            data-testid="change-password-new"
            className="mt-2 w-full border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-2 text-sm outline-none" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">Confirm new password</span>
          <input type="password" required minLength={10} value={next2} onChange={(e) => setNext2(e.target.value)}
            data-testid="change-password-confirm"
            className="mt-2 w-full border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-2 text-sm outline-none" />
        </label>
        <button disabled={pwBusy} data-testid="change-password-submit"
          className="bg-[#111] text-white px-8 py-4 font-semibold hover:bg-[#99582A] disabled:opacity-50">
          {pwBusy ? "Updating…" : "Update password"}
        </button>
      </form>

      {/* MFA */}
      <div className="pt-8 border-t border-[#E7E2D9] max-w-2xl">
        <p className="text-xs uppercase tracking-[0.25em] text-[#99582A] font-semibold">Two-factor authentication (2FA)</p>
        {info?.mfa_enabled ? (
          <form onSubmit={disableMfa} className="mt-4 space-y-4 max-w-md" data-testid="mfa-disable-form">
            <p className="text-sm text-[#4A4A4A]">
              2FA is active. Every sign-in requires a 6-digit code from your authenticator app.
            </p>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">Confirm password to disable</span>
              <input type="password" required value={disablePw} onChange={(e) => setDisablePw(e.target.value)}
                data-testid="mfa-disable-password"
                className="mt-2 w-full border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-2 text-sm outline-none" />
            </label>
            <button disabled={mfaBusy} data-testid="mfa-disable-submit"
              className="border border-red-600 text-red-600 px-6 py-3 text-sm font-semibold hover:bg-red-50 disabled:opacity-50">
              Disable 2FA
            </button>
          </form>
        ) : mfa ? (
          <form onSubmit={confirmMfa} className="mt-4 space-y-4" data-testid="mfa-enable-form">
            <p className="text-sm text-[#4A4A4A]">
              1. Open Google Authenticator (or any TOTP app) and add an account using this setup key:
            </p>
            <code className="block bg-[#FBF8F3] border border-[#E7E2D9] p-3 text-sm font-mono select-all" data-testid="mfa-secret">
              {mfa.secret}
            </code>
            <p className="text-xs text-[#666] break-all">Or paste this link into your app: {mfa.otpauth_url}</p>
            <label className="block max-w-md">
              <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">2. Enter the 6-digit code to confirm</span>
              <input inputMode="numeric" required value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
                data-testid="mfa-enable-code"
                className="mt-2 w-full border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-2 text-sm outline-none tracking-[0.4em]" />
            </label>
            <div className="flex gap-3">
              <button disabled={mfaBusy} data-testid="mfa-enable-submit"
                className="bg-[#111] text-white px-6 py-3 text-sm font-semibold hover:bg-[#99582A] disabled:opacity-50">
                Enable 2FA
              </button>
              <button type="button" onClick={() => { mfaCancel().catch(() => {}); setMfa(null); }} data-testid="mfa-cancel-btn" className="border border-[#E7E2D9] px-6 py-3 text-sm">
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-4">
            <p className="text-sm text-[#4A4A4A] max-w-xl">
              Add a second layer of protection: after your password, sign-in will also require a
              time-based code from an authenticator app on your phone.
            </p>
            <button onClick={startMfa} disabled={mfaBusy} data-testid="mfa-setup-btn"
              className="mt-4 bg-[#111] text-white px-6 py-3 text-sm font-semibold hover:bg-[#99582A] disabled:opacity-50 inline-flex items-center gap-2">
              <ShieldCheck size={15} /> {mfaBusy ? "Preparing…" : "Set up 2FA"}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// ==================== helpers ====================
function Field({ label, value, onChange, textarea, testId }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">{label}</span>
      {textarea ? (
        <textarea rows={4} data-testid={testId} value={value || ""} onChange={(e)=>onChange(e.target.value)} className="mt-2 w-full border border-[#E7E2D9] p-3 text-sm focus:outline-none focus:border-[#99582A]"/>
      ) : (
        <input type="text" data-testid={testId} value={value || ""} onChange={(e)=>onChange(e.target.value)} className="mt-2 w-full border-b-2 border-[#E7E2D9] focus:border-[#99582A] py-2 text-sm outline-none"/>
      )}
    </label>
  );
}
